// src/components/adpartners/NetworkStatsTable.js

import React, { useState, useEffect, useRef } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import Select from "react-select";
import ScrollContainer from "react-indiana-drag-scroll";
import { CSVLink } from "react-csv";
import { SlButton, SlIcon } from "@shoelace-style/shoelace/dist/react";

import API from "../../services/api";
import TableSkelton from "../../shared/TableSkelton";
import NoDataFound from "../../shared/NoDataFound";
import Loader from "../../shared/Loader";
import AdPartnerCharts from "./AdPartnerCharts";
import { Toast } from "../../shared/Toast";
import { toLakhs, newspaperLangs } from "../../utils/constants";

const headers = [
  { label: "Date", key: "Date" },
  { label: "Partner", key: "Partner" },
  { label: "AdCode", key: "AdCode" },
  { label: "Language", key: "Language" },
  { label: "Requests", key: "Requests" },
  { label: "Revenue", key: "Revenue" },
  { label: "Impressions", key: "Impressions" },
  { label: "Clicks", key: "Clicks" },
  { label: "eCPM", key: "eCPM" },
  { label: "Position", key: "Position" },
];

const sample_csv_data = [
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 2625961, Revenue: 786, Impressions: 1070735, Clicks: 19208, eCPM: 0.62, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 2540107, Revenue: 566, Impressions: 1050633, Clicks: 18804, eCPM: 0.55, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 1386724, Revenue: 316, Impressions: 674675, Clicks: 10608, eCPM: 0.49, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 633449, Revenue: 136, Impressions: 376867, Clicks: 4624, eCPM: 0.36, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 355002, Revenue: 65, Impressions: 195930, Clicks: 2588, eCPM: 0.32, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 296635, Revenue: 50, Impressions: 144352, Clicks: 2068, eCPM: 0.34, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 779679, Revenue: 146, Impressions: 391907, Clicks: 6365, eCPM: 0.37, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 806863, Revenue: 178, Impressions: 421030, Clicks: 7328, eCPM: 0.42, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 1962204, Revenue: 521, Impressions: 1029204, Clicks: 18302, eCPM: 51, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 3352902, Revenue: 918, Impressions: 1666659, Clicks: 34731, eCPM: 0.55, Position: 1 },
  { Date: "11/12/2025", Partner: "Meta", AdCode: "META_TEL_01", Language: "Telugu", Requests: 2120926, Revenue: 526, Impressions: 985675, Clicks: 19102, eCPM: 0.53, Position: 1 },
];

const NetworkStatsTable = ({ dateRange: parentDateRange, lang_id: parentLangId, setShowStats, setAdPartnerData, setEditStatsData, loadData: refreshParentData }) => {
  const [data, setData] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lang_id, setLangId] = useState(parentLangId || { value: 1, label: "Telugu" });
  const [expandedRow, setExpandedRow] = useState(null);
  const [positionData, setPositionData] = useState([]);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const [dateRange, setDateRange] = useState(parentDateRange);

  useEffect(() => {
    setDateRange(parentDateRange);
  }, [parentDateRange]);

  useEffect(() => {
    setLangId(parentLangId);
  }, [parentLangId]);

  const loadData = async () => {
    setIsDataLoading(true);
    try {
      const res = await API({
        url: "/adpartners/data",
        method: "POST",
        data: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          lang_id: lang_id.value,
        },
      });
      if (res.code === 200) {
        setData(res.data || []);
      } else {
        Toast("error", res.message || "Failed to load data");
      }
    } catch (err) {
      Toast("error", "Network error");
    } finally {
      setIsDataLoading(false);
    }
  };

  const loadPositionData = async (ad_partner_id) => {
    try {
      const res = await API({
        url: `/adpartners/data/${ad_partner_id}`,
        method: "POST",
        data: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          lang_id: lang_id.value,
          ad_partner_id,
        },
      });
      if (res.code === 200) {
        setPositionData(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleRow = async (ad_partner_id) => {
    if (expandedRow === ad_partner_id) {
      setExpandedRow(null);
      setPositionData([]);
    } else {
      setExpandedRow(ad_partner_id);
      await loadPositionData(ad_partner_id);
    }
  };

  const handleEditPositionStats = (posData, partner) => {
    setEditStatsData({
      ...posData,
      ad_partner_id: partner.ad_partner_id,
      ad_partner_name: partner.ad_partner_name,
      id: posData.id,
      position: posData.position,
    });

    setAdPartnerData({
      ad_partner_id: partner.ad_partner_id,
      ad_partner_name: partner.ad_partner_name,
      ad_code: partner.ad_code || null,
    });

    setShowStats(true);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleFileUpload = async () => {
    if (!file) return Toast("error", "No file selected");

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const res = await API({
        url: "/adpartners/file",
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.code === 200) {
        Toast("success", res.message || "File uploaded");
        setFile(null);
        fileInputRef.current.value = "";
        loadData();
        refreshParentData?.(); // Refresh parent if needed
      } else {
        Toast("error", res.message || "Upload failed");
      }
    } catch (err) {
      Toast("error", "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDateRange = (newRange) => {
    setDateRange(newRange);
  };

  useEffect(() => {
    loadData();
    setExpandedRow(null);
    setPositionData([]);
  }, [dateRange, lang_id]);

  const isSingleDay = dateRange?.startDate === dateRange?.endDate;

  return (
    <div>
      <aside className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h6 className="text-xl font-bold text-gray-800">Network Ads Stats</h6>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="w-40">
            <Select value={lang_id} onChange={setLangId} options={newspaperLangs} className="text-sm" />
          </div>
          <div className="border rounded-lg border-gray-300 hover:border-cyan-500 w-64">
            <Datepicker
              value={dateRange}
              onChange={handleDateRange}
              primaryColor="cyan"
              useRange={true}
              showShortcuts={true}
              configs={{
                shortcuts: {
                  today: "Today",
                  yesterday: "Yesterday",
                  currentMonth: "Current Month",
                  pastMonth: "Last Month",
                },
              }}
            />
          </div>
          <CSVLink data={sample_csv_data} headers={headers} filename="sample_ad_partners_stats.csv" className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded text-sm font-medium">
            Download Sample CSV
          </CSVLink>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="text-sm text-gray-600 file:py-2 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
            />
            <SlButton variant="primary" size="small" onClick={handleFileUpload} disabled={uploading || !file}>
              {uploading ? <Loader /> : "Upload File"}
            </SlButton>
          </div>
        </div>
      </aside>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <ScrollContainer>
          <table className="table table-bordered w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">S.No</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Partner</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ad Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Requests</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Impressions</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Clicks</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">eCPM</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Revenue (USD)</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Revenue (INR)</th>
              </tr>
            </thead>

            {isDataLoading ? (
              <TableSkelton rowSpan={8} colspan={9} />
            ) : data.length === 0 ? (
              <NoDataFound colspan={9} />
            ) : (
              <tbody>
                {data.map((row, idx) => (
                  <React.Fragment key={row.ad_partner_id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 cursor-pointer" onClick={() => handleToggleRow(row.ad_partner_id)}>
                        <div className="flex items-center gap-2">
                          <SlIcon library="lucide" name={expandedRow === row.ad_partner_id ? "chevron-up" : "chevron-down"} className="text-cyan-500" />
                          {idx + 1}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.ad_partner_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.ad_code || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={row.requests}>
                        {toLakhs(row.requests)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={row.impressions}>
                        {toLakhs(row.impressions)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={row.clicks}>
                        {toLakhs(row.clicks)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.ecpm ? Number(row.ecpm).toFixed(2) : "0.00"}</td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={row.revenue}>
                        {toLakhs(row.revenue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={row.revenue_inr}>
                        {toLakhs(row.revenue_inr)}
                      </td>
                    </tr>

                    {expandedRow === row.ad_partner_id && positionData.length > 0 && (
                      <tr>
                        <td colSpan={9} className="p-0">
                          <div className="bg-gray-50 p-4">
                            <table className="table table-bordered w-full rounded-lg shadow-md">
                              <thead className="bg-yellow-200">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Position</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Requests</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Impressions</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Clicks</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">eCPM</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Revenue (USD)</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Revenue (INR)</th>
                                  {isSingleDay && <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Action</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {positionData.map((pos, i) => (
                                  <tr key={i} className="border-b hover:bg-gray-100">
                                    <td className="px-4 py-2 text-sm text-gray-600">{pos.position}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600" title={pos.requests}>
                                      {toLakhs(pos.requests)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600" title={pos.impression}>
                                      {toLakhs(pos.impression)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600" title={pos.clicks}>
                                      {toLakhs(pos.clicks)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{pos.ecpm ? Number(pos.ecpm).toFixed(2) : "0.00"}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600" title={pos.revenue}>
                                      {toLakhs(pos.revenue)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600" title={pos.revenue_inr}>
                                      {toLakhs(pos.revenue_inr)}
                                    </td>
                                    {isSingleDay && (
                                      <td className="px-4 py-2 text-center">
                                        <div className="flex flex-col items-center cursor-pointer" onClick={() => handleEditPositionStats(pos, row)}>
                                          <SlIcon library="lucide" name="edit" className="text-gray-600 hover:text-cyan-500" />
                                          <span className="text-xs text-gray-500">Edit</span>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                <tr className="font-bold bg-gray-100 text-gray-800">
                  <td colSpan={2} className="px-4 py-3 text-right text-sm">
                    Totals
                  </td>
                  <td className="px-4 py-3 text-sm">{toLakhs(data.reduce((a, r) => a + Number(r.requests || 0), 0))}</td>
                  <td className="px-4 py-3 text-sm">{toLakhs(data.reduce((a, r) => a + Number(r.impressions || 0), 0))}</td>
                  <td className="px-4 py-3 text-sm">{toLakhs(data.reduce((a, r) => a + Number(r.clicks || 0), 0))}</td>
                  <td className="px-4 py-3 text-sm">{(data.reduce((a, r) => a + Number(r.ecpm || 0), 0) / (data.length || 1)).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm">{toLakhs(data.reduce((a, r) => a + Number(r.revenue || 0), 0))}</td>
                  <td className="px-4 py-3 text-sm">{toLakhs(data.reduce((a, r) => a + Number(r.revenue_inr || 0), 0))}</td>
                </tr>
              </tbody>
            )}
          </table>
        </ScrollContainer>

        <AdPartnerCharts data={data} />
      </div>
    </div>
  );
};

export default NetworkStatsTable;
